// The Monday weekly review: written by Claude from the portal every Monday morning, kept as history, read by the whole team.
import { useState } from "react";
import { ago, api, Empty, fmtDate, Loading, Pill, useLoad, useMe } from "./lib";
import { StrategyScorecard } from "./strategy";

const GOAL: Record<string, { label: string; tone: string }> = { on_track: { label: "On track", tone: "good" }, behind: { label: "Behind", tone: "bad" }, unknown: { label: "Unknown", tone: "" } };

export function WeeklyReview() {
  const { toast } = useMe();
  const { data, error, reload } = useLoad<any>("/api/reviews");
  const [pick, setPick] = useState(0);
  const [busy, setBusy] = useState(false);
  if (!data) return <div className="page"><Loading error={error} /></div>;
  const r = data.reviews[pick];
  const write = async () => {
    setBusy(true);
    try {
      await api("/api/reviews/generate", { method: "POST" });
      setPick(0);
      reload();
    } catch (e: any) {
      toast(e.message, true);
    } finally {
      setBusy(false);
    }
  };
  const label = (t: string) => <div className="tiny" style={{ fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 4 }}>{t}</div>;
  return (
    <div className="page">
      <div className="row between">
        <div className="row">
          {data.reviews.length > 1 && (
            <select className="input" style={{ width: "auto" }} value={pick} onChange={(e) => setPick(Number(e.target.value))} aria-label="Week">
              {data.reviews.map((x: any, i: number) => <option key={x.id} value={i}>Week of {fmtDate(x.week_of)}</option>)}
            </select>
          )}
        </div>
        {data.can_write && data.connected && <button className="btn" disabled={busy} onClick={write}>{busy ? "Writing…" : data.reviews.length ? "Write it again now" : "Write this week's review"}</button>}
      </div>
      <StrategyScorecard />
      {!r ? (
        <Empty>The first review arrives Monday morning. Claude writes it from what's in the portal: the goal, each team's plan and dated work, and what got finished.</Empty>
      ) : (
        <>
          <section className="card stack ai-card">
            <div><span className="ai-spark" /><strong className="ai-mark">Week of {fmtDate(r.week_of)}</strong><span className="muted tiny"> · written {ago(r.created_at)} · AI summary, check it</span></div>
            <div style={{ fontSize: 17, fontWeight: 600 }}>{r.headline}</div>
            <div className="row" style={{ gap: 8 }}><Pill tone={GOAL[r.goal.status]?.tone}>{GOAL[r.goal.status]?.label}</Pill><span className="small">{r.goal.line}</span></div>
          </section>
          <div className="grid">
            <section className="card stack">
              {label("Done last week")}
              {r.done.length ? <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>{r.done.map((x: string, i: number) => <li key={i}>{x}</li>)}</ul> : <span className="muted small">Nothing recorded as finished.</span>}
            </section>
            <section className="card stack">
              {label("Slipped")}
              {r.slipped.length ? <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>{r.slipped.map((x: string, i: number) => <li key={i}>{x}</li>)}</ul> : <span className="muted small">Nothing late.</span>}
            </section>
          </div>
          <section className="card stack">
            {label("This week")}
            {r.this_week.length ? (
              <div className="list">
                {r.this_week.map((x: any, i: number) => (
                  <div key={i} className="item">
                    <div className="grow"><div className="title">{x.title}</div></div>
                    <Pill>{x.who}</Pill>
                    {x.by && <Pill>{/^\d{4}-\d{2}-\d{2}$/.test(x.by) ? fmtDate(x.by) : x.by}</Pill>}
                  </div>
                ))}
              </div>
            ) : <span className="muted small">Nothing dated.</span>}
          </section>
          <section className="card stack">
            {label("By team")}
            <div className="list">
              {r.departments.map((d: any, i: number) => (
                <div key={i} className="item" style={{ alignItems: "flex-start" }}>
                  <div className="grow">
                    <div className="title">{d.name}</div>
                    <div className="small muted">{d.status}</div>
                    <div className="small" style={{ marginTop: 2 }}><span style={{ color: "var(--accent-ink)", fontWeight: 600 }}>Focus: </span>{d.focus}</div>
                  </div>
                </div>
              ))}
            </div>
          </section>
          <p className="muted tiny" style={{ margin: 0 }}>Written from the portal only: the goal, each team's plan and dated work, and what finished. Inbox items, decisions and finance aren't included, since the whole team reads this.</p>
        </>
      )}
    </div>
  );
}
